/**
 * Das Wasser.
 *
 * Vorher war die See eine schwarze Fläche, und damit fehlte der Szene das, woran man
 * nachts als Erstes erkennt, dass man auf dem Wasser ist: die Spiegelung. Ein Licht über
 * der Wasserfläche zieht eine Bahn zum Betrachter hin, und diese Bahn verrät Richtung und
 * Entfernung, lange bevor man den Rumpf sieht.
 *
 * Gerechnet wird das im Fragmentshader, ohne zweiten Durchgang und ohne Spiegelkamera:
 * Für jedes Licht liegt der Mittelpunkt seiner Bahn dort, wo die Gerade vom Auge zum
 * gespiegelten Licht die Wasserfläche trifft. Um diesen Punkt wird ein in Blickrichtung
 * gestreckter Fleck gezeichnet und mit dem Wellenmuster zerhackt, damit daraus Glitzern
 * wird statt eines Flecks. Das ist die klassische Näherung für flaches Wasser, und sie
 * kostet nichts.
 *
 * Die Wellen kommen aus der gelieferten Normalmap (`assets/texturen/wasser-normal.png`),
 * zwei Lagen derselben Kachel in verschiedener Größe und gegenläufiger Drift. Vorher
 * waren es drei Sinuslagen, und die sind als Fläche zu glatt: eine Sinuswelle hat keine
 * Kräusel, und genau die machen aus einer blauen Fläche Wasser. Die Gischtmaske
 * (`wasser-gischt.png`) sitzt auf derselben Kachel und derselben Phase und setzt die
 * Kämme.
 *
 * Gerechnet wird in Koordinaten relativ zu einem Ursprung, der dem Auge folgt und dabei
 * auf Vielfache der groben Kachel einrastet. Ohne das wären die Texturkoordinaten bis zu
 * 11000 groß, und in `mediump` läge zwischen zwei benachbarten Werten mehr als eine halbe
 * Kachel: die Wellen zerfielen zu Stufen. Das Einrasten hält das Muster trotzdem fest im
 * Wasser stehen, statt es mit der Kamera mitzuziehen.
 *
 * Die Wellen bewegen sich mit der Zeit der Szene, nicht mit der Uhr des Rechners: im
 * Prüfmodus steht die Zeit still, und damit steht auch das Wasser, sonst wären die
 * Pixelprüfungen nicht wiederholbar.
 */
import { Color, DoubleSide, Mesh, PlaneGeometry, ShaderMaterial, Texture, Vector2, Vector3 } from 'three'
import { textur, type TexturQuelle } from './texturen'

/** Kantenlänge der beiden Wellenlagen in Metern. Die grobe ist das Raster des Ursprungs. */
export const WELLE_FEIN_M = 24
export const WELLE_GROB_M = 96

/** So viele Lichter spiegeln sich; mehr sind in einem Bild ohnehin nicht zu trennen. */
export const MAX_REFLECTIONS = 8

export interface WaterLight {
  position: Vector3
  colour: Color
  /** 0 bis 1, wie hell das Licht gerade brennt. */
  intensity: number
}

export interface WaterPalette {
  deep: Color
  horizon: Color
  /** Farbe des Mondlichts, Nacht, beziehungsweise der Sonne am Tag. */
  sheen: Color
}

const VERTEX = `
varying vec3 vWorld;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorld = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`

const FRAGMENT = `
// highp: die Wasserfläche reicht 22 Kilometer weit, und in mediump verlöre die
// Texturkoordinate dort ihre Nachkommastellen.
precision highp float;

uniform vec3 uDeep;
uniform vec3 uHorizon;
uniform vec3 uSheen;
uniform float uTime;
uniform float uDay;
uniform float uMotion;
uniform float uFogDensity;
uniform vec3 uFogColour;
uniform vec3 uMoon;
uniform sampler2D uWellen;
uniform sampler2D uGischt;
uniform float uHatKarten;
uniform vec2 uUrsprung;
uniform int uLightCount;
uniform vec2 uLightMid[${MAX_REFLECTIONS}];
uniform vec2 uLightAxis[${MAX_REFLECTIONS}];
uniform vec3 uLightColour[${MAX_REFLECTIONS}];
uniform vec3 uLightShape[${MAX_REFLECTIONS}];

varying vec3 vWorld;

/**
 * Die Wellen: ein Griff in die gelieferte Kachel für die Kräusel, zwei Sinus für die
 * Dünung darunter. Rückgabe: die Neigung der Fläche in x und z, dazu ein Ersatz für die
 * Höhe.
 *
 * Die Aufteilung ist eine Rechnung, keine Bequemlichkeit: ein zweiter Texturgriff für
 * die lange Welle kostete auf dieser Maschine gemessen vier Millisekunden je Bild, und
 * die lange Welle ist genau das, was ein Sinus gut kann. Was er nicht kann, ist das
 * Kräuseln, und das kommt aus der Lieferung.
 *
 * Die Kachel wächst mit dem Abstand von 24 auf 96 Meter. Bliebe sie bei 24, fiele sie
 * weiter draußen unter die Größe eines Bildpunkts, und die Mipmap machte daraus eine
 * graue Fläche: die See hätte ab ein paar hundert Metern keine Struktur mehr. Mit der
 * größeren Kachel bleibt dort sichtbar, was man dort sieht, nämlich die Dünung.
 */
float kachelFuer(float abstand) {
  return mix(${WELLE_FEIN_M}.0, ${WELLE_GROB_M}.0, smoothstep(120.0, 700.0, abstand));
}

/** Dieselbe Kachel, dieselbe Drift: Welle und Gischt müssen in einer Phase laufen. */
vec2 wellenUv(vec2 p, float t, float abstand) {
  return p / kachelFuer(abstand) + vec2(0.021, 0.013) * t;
}

vec3 wellen(vec2 p, float t, float abstand) {
  vec3 a = texture2D(uWellen, wellenUv(p, t, abstand)).xyz * 2.0 - 1.0;
  float lang = sin(dot(p, vec2(0.021, 0.011)) + t * 0.55) * 0.6
             + sin(dot(p, vec2(-0.013, 0.026)) + t * 0.8) * 0.4;
  vec2 duenung = vec2(
    cos(dot(p, vec2(0.021, 0.011)) + t * 0.55) * 0.021,
    cos(dot(p, vec2(-0.013, 0.026)) + t * 0.8) * 0.026
  );
  return vec3(a.xy * 0.075 + duenung, (a.x + a.y) * 0.95 + lang * 0.8);
}

/**
 * Die Gischtmaske. Sie sitzt auf derselben Kachel und derselben Phase wie die Welle,
 * so verlangt es der Kanalvertrag: ihre Kämme gehören zu genau diesen Wellen.
 */
float gischt(vec2 p, float t, float abstand) {
  return texture2D(uGischt, wellenUv(p, t, abstand)).r;
}

void main() {
  vec3 auge = cameraPosition;
  vec3 zumAuge = auge - vWorld;
  float abstand = length(zumAuge);
  float t = uTime * uMotion;

  // Struktur gibt es nur in der Nähe. Weiter draußen würde die Welle kleiner als ein
  // Pixel und zerfiele in Streifen, und die Rechnung wäre verschenkt: die Kachel wird
  // deshalb nur dort gelesen, wo man sie auch sieht. Bei 900 Metern ist der Beitrag auf
  // drei Prozent abgefallen, und weiter draußen steht ohnehin der Nebel. Fehlt die
  // Lieferung, bleibt die Fläche glatt statt leer.
  float neigung = 0.0;
  float hoehe = 0.0;
  float schaum = 0.0;
  vec2 lokal = vWorld.xz - uUrsprung;
  if (abstand < 900.0 && uHatKarten > 0.5) {
    vec3 w = wellen(lokal, t, abstand);
    float nah = exp(-abstand / 260.0);
    // Die Neigung zählt längs der Blickachse: quer dazu sieht man sie nicht.
    vec2 blick = normalize(vWorld.xz - auge.xz + vec2(1e-5));
    neigung = dot(w.xy, blick) * nah;
    hoehe = w.z * nah;
    // Schaum nur ganz nah, sonst wird aus den Kämmen ein grauer Schleier. Die Grenze
    // liegt dort, wo der Abfall darunter nichts mehr beiträgt: der zweite Texturgriff
    // je Bildpunkt lohnt weiter draußen nicht.
    if (abstand < 500.0) schaum = gischt(lokal, t, abstand) * exp(-abstand / 120.0);
  }

  // Grundfarbe: nah dunkel, zum Horizont hin heller, und zur Seite des Mondes etwas
  // heller als von ihm weg.
  float fern = clamp(abstand / 2600.0, 0.0, 1.0);
  vec3 farbe = mix(uDeep, uHorizon, pow(fern, 0.55));
  float mondSeite = clamp(dot(normalize(vWorld.xz - auge.xz + vec2(1e-5)), normalize(uMoon.xz)), 0.0, 1.0);
  farbe += uSheen * mondSeite * fern * (0.035 + 0.05 * uDay);

  // Struktur: die Wellen heben und senken die Helligkeit.
  farbe += uSheen * (0.02 + 0.06 * uDay) * hoehe;

  // Die Kämme. Nachts bleibt davon fast nichts: ein Schaumkamm ist kein Licht, und die
  // Lichter müssen das Hellste im Bild bleiben.
  farbe += uSheen * schaum * (0.012 + 0.10 * uDay);

  // Die Mondbahn: dasselbe wie eine Spiegelung, nur steht die Quelle unendlich weit weg.
  // Ihre Bahn zieht sich deshalb vom Horizont bis vor den Betrachter, und sie ist das,
  // woran ein Bild als Wasser zu erkennen ist.
  vec2 mondAchse = normalize(uMoon.xz + vec2(1e-5));
  vec2 zumPunkt = vWorld.xz - auge.xz;
  float mLaengs = dot(zumPunkt, mondAchse);
  float mQuer = dot(zumPunkt, vec2(-mondAchse.y, mondAchse.x));
  // Je weiter weg, desto breiter die Bahn: das ist die Perspektive, nicht die Physik.
  float mBreite = 3.0 + max(mLaengs, 0.0) * 0.10;
  float mondBahn = step(0.0, mLaengs) * exp(-(mQuer * mQuer) / (mBreite * mBreite));
  float mondUnruhe = sin(mLaengs * 0.9 - t * 1.3) * 0.012 + sin(mLaengs * 2.7 + mQuer * 1.5 + t * 2.2) * 0.008;
  float mondChop = 0.25 + 0.75 * smoothstep(-0.02, 0.03, neigung + mondUnruhe);
  farbe += uSheen * mondBahn * mondChop * (0.10 + 0.30 * uDay);

  // Spiegelungen der Lichter. Mittelpunkt, Achse, Breite, Länge und Stärke rechnet die
  // Anwendung je Bild einmal aus; hier bleibt nur das, was wirklich vom Pixel abhängt.
  // Weiter als vier Kilometer braucht es das gar nicht: dort ist ohnehin nichts zu sehen.
  if (abstand < 4000.0) {
    for (int i = 0; i < ${MAX_REFLECTIONS}; i++) {
      if (i >= uLightCount) break;
      vec2 achse = uLightAxis[i];
      vec2 rel = vWorld.xz - uLightMid[i];
      float laengs = dot(rel, achse);
      float quer = dot(rel, vec2(-achse.y, achse.x));

      // Die Bahn ist schmal und zieht sich zum Betrachter hin, nicht von ihm weg.
      float breite = uLightShape[i].x;
      float laenge = uLightShape[i].y * (laengs < 0.0 ? 1.0 : 0.35);
      float bahn = exp(-(quer * quer) / (breite * breite)) * exp(-(laengs * laengs) / (laenge * laenge));
      if (bahn < 0.004) continue;

      // Zerhacken, damit Glitzern daraus wird statt eines Flecks.
      float unruhe = sin(laengs * 1.7 - t * 2.0) * 0.010 + sin(laengs * 4.3 + quer * 2.1 + t * 3.1) * 0.006;
      float chop = 0.30 + 0.70 * smoothstep(-0.018, 0.028, neigung + unruhe);
      farbe += uLightColour[i] * bahn * chop * uLightShape[i].z;
    }
  }

  // Nebel wie in der übrigen Szene, damit der Horizont nicht als Kante steht.
  float nebel = 1.0 - exp(-uFogDensity * uFogDensity * abstand * abstand);
  farbe = mix(farbe, uFogColour, clamp(nebel, 0.0, 1.0));

  gl_FragColor = vec4(farbe, 1.0);
}
`

export interface WaterHandle {
  mesh: Mesh
  setPalette(palette: WaterPalette, fogColour: Color, fogDensity: number, dayFactor: number): void
  /** Wellen an, Wellen aus: im Prüfmodus und bei "weniger Bewegung" steht das Wasser. */
  setMotion(on: boolean): void
  /** Bindet die gelieferten Wellenbilder, sobald sie geladen sind. */
  bindeKarten(): void
  update(timeSec: number, lights: WaterLight[], moon: Vector3, eye: Vector3): void
  dispose(): void
}

export function createWater(size = 44000, quelle: TexturQuelle = textur): WaterHandle {
  const uniforms = {
    uDeep: { value: new Color(0x040910) },
    uHorizon: { value: new Color(0x0a1424) },
    uSheen: { value: new Color(0x9fc0ff) },
    uTime: { value: 0 },
    uDay: { value: 0 },
    uMotion: { value: 1 },
    uFogDensity: { value: 0.00006 },
    uFogColour: { value: new Color(0x0a1424) },
    uMoon: { value: new Vector3(0.55, 0.13, -1).normalize() },
    uWellen: { value: null as Texture | null },
    uGischt: { value: null as Texture | null },
    uHatKarten: { value: 0 },
    uUrsprung: { value: new Vector2() },
    uLightCount: { value: 0 },
    uLightMid: { value: Array.from({ length: MAX_REFLECTIONS }, () => new Vector2()) },
    uLightAxis: { value: Array.from({ length: MAX_REFLECTIONS }, () => new Vector2(0, 1)) },
    uLightColour: { value: Array.from({ length: MAX_REFLECTIONS }, () => new Color()) },
    uLightShape: { value: Array.from({ length: MAX_REFLECTIONS }, () => new Vector3(1, 1, 0)) },
  }

  const material = new ShaderMaterial({
    uniforms,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    side: DoubleSide,
    fog: false,
  })

  /**
   * Die Wellen kommen aus der Lieferung. Solange sie nicht geladen ist, bleibt die
   * Fläche glatt: ein Wasser ohne Kräusel ist besser als eine Szene, die auf eine Datei
   * wartet. `bindeKarten()` holt das nach, sobald `preloadTexturen()` durch ist.
   */
  function bindeKarten(): void {
    const wellen = quelle('wasser-normal')
    const gischt = quelle('wasser-gischt')
    if (!wellen || !gischt) return
    uniforms.uWellen.value = wellen
    uniforms.uGischt.value = gischt
    uniforms.uHatKarten.value = 1
    material.needsUpdate = true
  }
  bindeKarten()

  // Ein Raster, damit die Wellenrechnung überhaupt Stützstellen hat; grob genug, dass
  // auch ein Telefon sie zeichnet.
  const mesh = new Mesh(new PlaneGeometry(size, size, 96, 96), material)
  mesh.rotation.x = -Math.PI / 2
  mesh.frustumCulled = false
  /**
   * Das Wasser zuerst, der Himmel danach. Beide sind undurchsichtig und stehen mit ihrem
   * Mittelpunkt im Ursprung, also entscheidet ohne diese Zeile die Reihenfolge des
   * Einhängens: der Himmel zeichnete die untere Bildhälfte fertig, und das Wasser malte
   * sie anschließend zu. Umgekehrt verwirft der Tiefentest die verdeckten Himmelspunkte,
   * bevor sie überhaupt gerechnet werden.
   */
  mesh.renderOrder = -1

  return {
    mesh,
    setPalette(palette, fogColour, fogDensity, dayFactor) {
      uniforms.uDeep.value.copy(palette.deep)
      uniforms.uHorizon.value.copy(palette.horizon)
      uniforms.uSheen.value.copy(palette.sheen)
      uniforms.uFogColour.value.copy(fogColour)
      uniforms.uFogDensity.value = fogDensity
      uniforms.uDay.value = dayFactor
    },
    setMotion(on) {
      uniforms.uMotion.value = on ? 1 : 0
    },
    bindeKarten,
    update(timeSec, lights, moon, eye) {
      uniforms.uTime.value = timeSec
      uniforms.uMoon.value.copy(moon)
      // Der Ursprung rastet auf die grobe Kachel ein: das Muster bleibt im Wasser stehen,
      // die Koordinate im Shader bleibt klein.
      uniforms.uUrsprung.value.set(
        Math.floor(eye.x / WELLE_GROB_M) * WELLE_GROB_M,
        Math.floor(eye.z / WELLE_GROB_M) * WELLE_GROB_M,
      )
      const hell = lights
        .filter((l) => l.intensity > 0.02 && l.position.y > 0)
        .sort((a, b) => b.intensity - a.intensity)
        .slice(0, MAX_REFLECTIONS)
      uniforms.uLightCount.value = hell.length
      hell.forEach((light, i) => {
        const mitte = reflectionCentre(eye, light.position)
        uniforms.uLightMid.value[i]!.copy(mitte)
        const achse = new Vector2(mitte.x - eye.x, mitte.y - eye.z)
        if (achse.lengthSq() < 1e-8) achse.set(0, 1)
        uniforms.uLightAxis.value[i]!.copy(achse.normalize())
        uniforms.uLightColour.value[i]!.copy(light.colour)
        const entfernung = Math.hypot(light.position.x - eye.x, light.position.z - eye.z)
        uniforms.uLightShape.value[i]!.set(
          0.6 + entfernung * 0.006,
          3 + entfernung * 0.1,
          (light.intensity * 0.75) / (1 + entfernung * 0.004),
        )
      })
    },
    dispose() {
      mesh.geometry.dispose()
      material.dispose()
    },
  }
}

/** Richtung, aus der das Mondlicht kommt. Fest, damit die Bahn im Bild stabil steht. */
export const MOON_DIRECTION = new Vector3(0.55, 0.13, -1).normalize()

export const WATER_NIGHT: WaterPalette = {
  deep: new Color(0x040b16),
  horizon: new Color(0x102237),
  sheen: new Color(0xaecbff),
}

export const WATER_DAY: WaterPalette = {
  deep: new Color(0x2f6b98),
  horizon: new Color(0x9dc2dd),
  sheen: new Color(0xffffff),
}

/** Für die Prüfung: dieselbe Rechnung wie im Shader, in TypeScript. */
export function reflectionCentre(eye: Vector3, light: Vector3): Vector2 {
  const height = Math.max(light.y, 0.4)
  const k = eye.y / (eye.y + height)
  return new Vector2(eye.x + (light.x - eye.x) * k, eye.z + (light.z - eye.z) * k)
}
